import { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useTranslation } from 'react-i18next';
import { Trash2, Edit, X, Plus, AlertTriangle } from 'lucide-react';
import { toast } from 'react-hot-toast';

export default function SubjectCategories() {
  const { api, user } = useAuth();
  const { t, i18n } = useTranslation('subjectcategories');
  const isRTL = i18n.language === 'ar';
  
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editName, setEditName] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Function to fetch categories
  const fetchCategories = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get('/api/v1/subject-categories');
      setCategories(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || t('errors.fetchFailed'));
      toast.error(t('errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (category) => {
    setCategoryToDelete(category);
    setIsDeleteModalOpen(true);
  };

  // Delete category
  const deleteCategory = async () => {
    try {
      await api.delete(`/api/v1/subject-categories/${categoryToDelete.id}`);
      setCategories(categories.filter(cat => cat.id !== categoryToDelete.id));
      toast.success(t('messages.deleteSuccess'));
      closeDeleteModal();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.deleteFailed'));
    }
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setCategoryToDelete(null);
  };

  // Open create modal
  const openCreateModal = () => {
    setNewCategoryName('');
    setIsCreateModalOpen(true);
  };

  // Create new category
  const createCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error(t('errors.emptyName'));
      return;
    }

    try {
      const response = await api.post('/api/v1/subject-categories', {
        category_name: newCategoryName,
        school_id: user.school_id
      });
      
      setCategories([...categories, response.data]);
      closeCreateModal();
      toast.success(t('messages.createSuccess'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.createFailed'));
    }
  };

  // Close create modal
  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setNewCategoryName('');
  };

  // Open edit modal
  const openEditModal = (category) => {
    setEditingCategory(category);
    setEditName(category.category_name);
    setIsEditModalOpen(true);
  };

  // Submit edit
  const submitEdit = async () => {
    if (!editName.trim()) {
      toast.error(t('errors.emptyName'));
      return;
    }

    try {
      const response = await api.put(`/api/v1/subject-categories/${editingCategory.id}`, {
        category_name: editName,
        school_id: user.school_id
      });
      
      setCategories(categories.map(cat => 
        cat.id === editingCategory.id ? { ...cat, category_name: editName } : cat
      ));
      
      closeEditModal();
      toast.success(t('messages.updateSuccess'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.updateFailed'));
    }
  };

  // Close edit modal
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingCategory(null);
    setEditName('');
  };

  // Loading state
  if (isLoading && categories.length === 0) {
    return (
      <div className="flex items-center justify-center p-10">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0E5252]"></div>
      </div>
    );
  }

  return (
    <div className={`max-w-6xl mx-auto ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('title')}</h1>
        <button
          onClick={openCreateModal}
          className={`flex items-center bg-[#0E5252] hover:bg-teal-700 text-white px-4 py-2 rounded-md transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
        >
          <Plus size={18} className={isRTL ? 'sm:ml-1' : 'sm:mr-1'} />
          <span className="hidden sm:inline">{t('buttons.addCategory')}</span>
        </button>
      </div>

      {error && (
        <div className={`mb-6 bg-red-50 border border-red-200 rounded-md p-4 flex items-start ${isRTL ? 'flex-row-reverse' : ''}`}>
          <AlertTriangle className={`text-red-500 mt-0.5 ${isRTL ? 'ml-2' : 'mr-2'}`} size={18} />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {categories.length === 0 && !isLoading ? (
        <div className="text-center py-10 bg-gray-50 rounded-md">
          <p className="text-gray-500 mb-4">{t('emptyState.noCategories')}</p>
          <button
            onClick={openCreateModal}
            className="bg-[#0E5252] hover:bg-teal-700 text-white px-4 py-2 rounded-md transition-colors"
          >
            {t('emptyState.addFirst')}
          </button>
        </div>
      ) : (
        <div className="bg-white border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('table.categoryName')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-left' : 'text-right'}`}>
                    {t('table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categories.map((category) => (
                  <tr key={category.id} className="hover:bg-gray-50">
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {category.category_name}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isRTL ? 'text-left' : 'text-right'}`}>
                      <div className={`flex space-x-2 ${isRTL ? 'justify-end space-x-reverse' : 'justify-end'}`}>
                        <button
                          onClick={() => openEditModal(category)}
                          className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                          title={t('tooltips.edit')}
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => openDeleteModal(category)}
                          className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                          title={t('tooltips.delete')}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Category Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className={`bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
            <div className={`flex justify-between items-center mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <h2 className="text-xl font-bold text-gray-800">{t('modals.create.title')}</h2>
              <button onClick={closeCreateModal} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); createCategory(); }}>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('labels.categoryName')}
                  </label>
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    required
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${isRTL ? 'text-right' : 'text-left'}`}
                    placeholder={t('placeholders.categoryName')}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>
              </div>
              <div className={`mt-6 flex space-x-3 ${isRTL ? 'justify-start space-x-reverse' : 'justify-end'}`}>
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('buttons.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0E5252] hover:bg-teal-700 text-white rounded-md"
                >
                  {t('buttons.add')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className={`bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
            <div className={`flex justify-between items-center mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <h2 className="text-xl font-bold text-gray-800">{t('modals.edit.title')}</h2>
              <button onClick={closeEditModal} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); submitEdit(); }}>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('labels.categoryName')}
                  </label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    required
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${isRTL ? 'text-right' : 'text-left'}`}
                    placeholder={t('placeholders.categoryName')}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>
              </div>
              <div className={`mt-6 flex space-x-3 ${isRTL ? 'justify-start space-x-reverse' : 'justify-end'}`}>
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('buttons.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0E5252] hover:bg-teal-700 text-white rounded-md"
                >
                  {t('buttons.update')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && categoryToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className={`bg-white rounded-lg shadow-xl max-w-md w-full p-6 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
            <div className={`flex justify-between items-center mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <h2 className="text-xl font-bold text-gray-800">{t('modals.delete.title')}</h2>
              <button onClick={closeDeleteModal} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
            <div className="mb-6">
              <p className={`text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('modals.delete.confirmation', { categoryName: categoryToDelete.category_name })}
              </p>
            </div>
            <div className={`flex space-x-3 ${isRTL ? 'justify-start space-x-reverse' : 'justify-end'}`}>
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                {t('buttons.cancel')}
              </button>
              <button
                onClick={deleteCategory}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md"
              >
                {t('buttons.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}